<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { useCalendarBulkAttendanceStore, useCalendarStore } from '@/store';
import CalendarBulkAttendance from './CalendarBulkAttendance.vue';
import { CALENDAR_COLOR, EMIT, PERMISSIONS } from '@/enum';
import { can, modifiedRRulePlugin, useDateTime } from '@/composables';

const emit = defineEmits<{
  (eventName: EMIT.DOCUMENT_EVENT, info: EventClickArg): void;
  (eventName: EMIT.EDIT_EVENT, info: EventClickArg): void;
  (eventName: EMIT.NEW_EVENT, info: DateClickArg): void;
  (eventName: EMIT.DAY_VIEW, info: Date): void;
  (eventName: EMIT.MOUNTED, info: string): void;
}>();
const calendar = ref();
const calendarStore = useCalendarStore();
const calendarBulkAttendanceStore = useCalendarBulkAttendanceStore();
const today = new Date();

defineExpose({ calendar });

onMounted(() => {
  setCalendarEvents();
});

const setCalendarEvents = () => {
  calendarOptions.value.events = calendarStore.events ? calendarStore.events : [];
};

watch(() => calendarStore.events, setCalendarEvents);

const calendarOptions = ref<CalendarOptions>({
  plugins: [timeGridPlugin, interactionPlugin, modifiedRRulePlugin],
  initialView: 'timeGridWeek',
  headerToolbar: false,
  allDaySlot: false,
  dayHeaderFormat: { day: 'numeric' },
  dayHeaderContent: (arg) =>
    calendarBulkAttendanceStore.defineBulkAttendance(arg, can([PERMISSIONS.THERAPY_RESULTS_BULK_UPDATE])),
  weekends: true,
  nowIndicator: true,
  height: '100%',
  expandRows: true,
  eventClassNames: (arg) => calendarStore.defineEventsClassNames(arg),
  eventShortHeight: 36,
  eventBackgroundColor: CALENDAR_COLOR.EVENT_BACKGROUND_COLOR,
  eventBorderColor: CALENDAR_COLOR.EVENT_BORDER_COLOR,
  eventTextColor: CALENDAR_COLOR.EVENT_TEXT_COLOR,
  eventMinHeight: 24,
  events: [],
  navLinks: true,
  navLinkDayClick: function (date) {
    emit(EMIT.DAY_VIEW, date);
  },
  dateClick: function (info: DateClickArg) {
    emit(EMIT.NEW_EVENT, info);
  },
  eventClick: (info: EventClickArg) => {
    const isDocumented = info.event._def.extendedProps.eventStatus === 'documented';
    const isInPast = (info.event.start as Date) < new Date();
    const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);
    const showDocumentEvent = isDocumented || isInPast;
    const isCaseloadAndInCurrentMonth =
      isCaseloadManagement && useDateTime().isSameMonthAndYear(info.event.start as Date);

    if (showDocumentEvent && !isCaseloadAndInCurrentMonth) {
      emit(EMIT.DOCUMENT_EVENT, info);
    } else {
      emit(EMIT.EDIT_EVENT, info);
    }
  },
  scrollTime: '07:00:00',
  viewDidMount: () => emit(EMIT.MOUNTED, 'week'),
});
</script>

<template>
  <div class="calendar-page-content">
    <CalendarBulkAttendance
      v-if="can([PERMISSIONS.THERAPY_RESULTS_BULK_UPDATE]) && calendarBulkAttendanceStore.displayBulkAttendance"
      @cancel="calendarBulkAttendanceStore.onBulkAttendanceCancel"
    />
    <div class="calendar-page-week-view">
      <FullCalendar
        ref="calendar"
        :options="calendarOptions"
        :class="'today-is-' + today.toLocaleDateString('en-us', { weekday: 'long' })"
      >
        <template #eventContent="arg">
          <div class="fc-event-main-frame">
            <div class="fc-event-time">
              {{ useDateTime().formatTimeByTimezone(arg.event.start, arg.event.extendedProps.hostTimezoneName) }} -
              {{ useDateTime().formatTimeByTimezone(arg.event.end, arg.event.extendedProps.hostTimezoneName) }}
              {{ arg.event.extendedProps.hostTimezone }}
            </div>
            <div class="fc-event-title-container">
              <div class="fc-event-title fc-sticky">{{ arg.event.title }}</div>
            </div>
          </div>
        </template>
      </FullCalendar>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/theme/extensions/calendar';

.calendar-page-week-view {
  padding-top: 2px;
  width: 100%;

  .fc-timegrid-cols {
    .fc-timegrid-col {
      &.fc-day-today {
        .fc-timegrid-col-frame {
          position: relative;
          z-index: 10;

          .fc-timegrid-col-bg {
            left: -4px;
            top: -35px;

            &::after {
              content: '';
              position: fixed;
              width: calc(20% - 44px);
              height: calc(100vh - 200px);
              border: 0.25rem solid rgba(var(--primary-color-medium), 0.5);
              border-radius: 0.5rem;
              z-index: 100;
              pointer-events: none;
            }
          }
        }
      }
    }
  }

  :deep(.fc) {
    .fc-view-harness {
      th {
        &.fc-day-today {
          color: var(--primary-color-dark);
        }
      }
    }
  }
}
</style>
